import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import GlobalConfig from '../../../../../config';

// manifests

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';

// props
import { IPaginationResourceShort } from '../../../../../props/general';
import { FilterOptionProps } from '../../../../uiframeworks/filters/panel';
import { IVesselCrewResourceShortProps } from '../../../../../props/vessels/crew';

// components
import { ActionButton, CommandBar, IColumn, ICommandBarItemProps, IContextualMenuItem, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import { useStore } from '../../../../../stores/root';
import Text from './../../../../typography/text';
import SelectedFilter from '../../../../uiframeworks/filters/selected';
import Pagination from '../../../../uiframeworks/pagination';
import VesselCrewService from '../../../../../services/vessels/crew';
import VesselCrewForm from '../form';
import DeactivateVesselCrewForm from '../form/deactivate';
import PermissionsService from '../../../../../services/permissions';
import VesselCrewStatus from '../../../../../manifests/status/vesselCrew';
import Tag from '../../../../uiframeworks/tag';
import moment from 'moment';
import PivotButton from '../../../../uiframeworks/buttons/pivot';

type VesselCrewsListProps = {
    vesselId: string;
    qs?: string[];
    crews?: IVesselCrewResourceShortProps[];
    columns?: TVesselCrewColumn[];
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideCreateButton?: boolean;
    hideQuickFilter?: boolean;
    hidePagination?: boolean;
    hideFilter?: boolean;
    variant?: 'card' | 'plain';
}

export type TVesselCrewColumn = 'user' | 'role' | 'status' | 'actions' | 'phoneNumber' | 'joinDate';

const VesselCrewsList: React.FC<VesselCrewsListProps> = (props: VesselCrewsListProps) => {
    const shownColumns: TVesselCrewColumn[] = props.columns || [
        'user',
        'phoneNumber',
        'joinDate',
        'role',
        'status',
        'actions'
    ];

    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [showingMore, setShowingMore] = React.useState<boolean>(false);
    const [crews, setCrews] = React.useState<IVesselCrewResourceShortProps[]>(props.crews || []);
    const [activeCrew, setActiveCrew] = React.useState<IVesselCrewResourceShortProps | undefined>();
    const [pagination, setPagination] = React.useState<IPaginationResourceShort | undefined>();
    const [selectedFilter, setSelectedFilter] = React.useState<FilterOptionProps[]>([]);
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const [selectedStatus, setSelectedStatus] = React.useState<'active' | 'deactive'>('active');

    const columns: IColumn[] = [
        {
            key: "user",
            name: "Name",
            fieldName: "user",
            minWidth: 250,
            maxWidth: 250,
            isMultiline: true,
            onRender: (item: IVesselCrewResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>{item.user.name}</Text>
                </Stack>
            }
        },
        {
            key: "actions",
            name: "",
            minWidth: 30,
            maxWidth: 30,
            onRender: (item: IVesselCrewResourceShortProps) => {
                const menuItems: IContextualMenuItem[] = [];

                if (PermissionsService.hasPermission(['vessels.update.all'], user.permissions)) {
                    menuItems.push({
                        key: 'deactivate',
                        text: "Deactivate",
                        iconProps: { iconName: "XMark" },
                        onClick: () => {
                            setActiveCrew(item);
                            setActiveSurface("vessel.crew.deactivate");
                        }
                    })
                }

                return <Stack.Item styles={{ root: { padding: '2px 0px' } }}>
                    {
                        menuItems.length > 0 ? <Stack.Item className={"detailsListActionRow"}>
                            <ActionButton className={'detailsListActionButton'} menuProps={{
                                items: menuItems
                            }} />
                        </Stack.Item> : null
                    }
                </Stack.Item >;
            }
        },
        {
            key: "phoneNumber",
            name: "Phone Number",
            minWidth: 150,
            maxWidth: 150,
            isMultiline: true,
            onRender: (item: IVesselCrewResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>{item.user.phoneNumber || "-"}</Text>
                </Stack>
            }
        },
        {
            key: "role",
            name: "Role",
            fieldName: "role",
            minWidth: 200,
            isMultiline: true,
            onRender: (item: IVesselCrewResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>{item.role.name}</Text>
                </Stack>
            }
        },
        {
            key: "joinDate",
            name: "Join Date",
            minWidth: 125,
            maxWidth: 125,
            isMultiline: true,
            onRender: (item: IVesselCrewResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>{moment(item.createdAt).format("DD/MM/YYYY")}</Text>
                </Stack>
            }
        },
        /*{
            key: "inactiveDate",
            name: "Inactive Date",
            minWidth: 125,
            maxWidth: 125,
            isMultiline: true,
            onRender: (item: IVesselCrewResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    {item.status === 'deactive' ? <Text size={'small'}>{moment(item.createdAt).format("DD/MM/YYYY")}</Text> : null}
                    {item.status !== 'deactive' ? <Text size={'small'}>-</Text> : null}
                </Stack>
            }
        },*/
        {
            key: "status",
            name: "Status",
            minWidth: 100,
            maxWidth: 100,
            isMultiline: true,
            onRender: (item: IVesselCrewResourceShortProps) => {
                const status = VesselCrewStatus.find((s) => s.key === item.status);

                return <Stack horizontal styles={{ root: { padding: '0px' } }}>
                    <Tag size={'small'} text={status?.text} variant={status?.tagVariant} />
                </Stack>
            }
        }
    ];

    useEffect(() => {
        if (!props.crews) {
            _onRetrieveCrews();
        } else {
            setLoaded(true);
        }
    }, [keyword, window.location.search]);

    const getQueryString = () => {
        let qs: string[] = JSON.parse(JSON.stringify(props.qs || []));
        const params = (new URLSearchParams(window.location.search));

        // get existing query string
        const _p = params.get("_p");

        qs.push(`top=all`);
        if ((_p || "").trim() !== "") { qs.push(`page=${_p}`) }
        if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }

        qs = qs.filter(function (item, pos) {
            return qs.indexOf(item) == pos;
        });

        return qs.join("&");
    }

    const _onRetrieveCrews = async () => {
        try {
            setLoaded(false);
            const qs = getQueryString();
            const results = await VesselCrewService.retrieve(props.vesselId, qs);

            setCrews(results.data);
            setPagination(results.pagination);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_vessel_crews_list_error',
                text: 'Failed to retrieve vessel crews list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        if (!props.hideSearch) {
            items.push({
                key: "search",
                onRender: () => {
                    return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} styles={{ root: { marginRight: 10 } }} />
                }
            });
        }

        farItems.push({
            key: "register",
            text: "Add crew",
            iconProps: { iconName: "Add" },
            onRender: () => {
                return <PrimaryButton text={"Add crew"}
                    iconProps={{ iconName: "Add" }}
                    styles={{ root: { marginLeft: 10 } }}
                    onClick={() => { setActiveSurface("vessel.crew.add") }} />;
            }
        });

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean, status?: string) => {
        setActiveSurface(undefined);
        setActiveCrew(undefined);

        if (refresh) {
            _onRetrieveCrews();
        }
    }

    const renderList = () => {
        const _crews = (crews || []).filter((c) => c.status === selectedStatus);

        return <>
            {
                !loaded || (loaded && _crews.length > 0) ? (
                    <>
                        <ShimmeredDetailsList
                            setKey="items"
                            items={_crews}
                            columns={columns.filter((col) => shownColumns.indexOf(col.key as TVesselCrewColumn) > -1)}
                            selectionMode={SelectionMode.none}
                            enableShimmer={!loaded}
                            onShouldVirtualize={() => false}
                            ariaLabelForShimmer="Content is being fetched"
                            ariaLabelForGrid="Item details" />
                    </>
                ) : null
            }
            {loaded && _crews.length < 1 ? <Stack styles={{ root: { padding: 10 } }}>
                <Text size={'small'}>Crews not found</Text>
            </Stack> : null}
        </>
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        {!props.hideCommandBar ? <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{
                root: {
                    padding: 0,
                    height: 'unset',
                    backgroundColor: 'transparent'
                }
            }}
            ariaLabel="Use left and right arrow keys to navigate between commands" /> : null}
        {selectedFilter.length > 0 ? <SelectedFilter selected={selectedFilter} onChange={(selected) => {
            setSelectedFilter(selected);
        }} /> : null}
        <Stack tokens={{ childrenGap: 10 }}>
            <PivotButton buttons={[
                {
                    key: "active",
                    text: "Active",
                    active: selectedStatus === 'active',
                    onClick: () => {
                        setSelectedStatus('active');
                    }
                },
                {
                    key: "deactive",
                    text: "Inactive",
                    active: selectedStatus === 'deactive',
                    onClick: () => {
                        setSelectedStatus('deactive');
                    }
                }
            ]} />
            {renderList()}
        </Stack>
        {activeSurface === 'vessel.crew.add' ? <VesselCrewForm vesselId={props.vesselId} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'vessel.crew.deactivate' && activeCrew ? <DeactivateVesselCrewForm vesselId={props.vesselId} crewId={activeCrew.id} onDismissed={_onSurfaceDismissed} /> : null}
        {/*activeSurface === 'engine.update' && activeEngine ? <VesselEngineForm vesselId={props.vesselId} engineId={activeEngine.id} onDismissed={_onSurfaceDismissed} /> : null*/}
    </Stack>;
};

export default VesselCrewsList;
